import React, { useState, useEffect } from 'react';
import DynamicGovernanceConfig from '../services/DynamicGovernanceConfig';
import { useOnboarding } from '../contexts/OnboardingContext';
import './GovernanceAdmin.css';

/**
 * Governance Admin Panel
 * Allows admins to manage dynamic governance parameters
 */
const GovernanceAdmin = ({ isOpen, onClose }) => {
  const { address: walletAddress } = useOnboarding();
  const [governanceStatus, setGovernanceStatus] = useState(null);
  const [configHistory, setConfigHistory] = useState([]);
  const [pendingProposals, setPendingProposals] = useState([]);
  const [circulatingSupply, setCirculatingSupply] = useState('');
  const [overrideParameter, setOverrideParameter] = useState('');
  const [overrideValue, setOverrideValue] = useState('');
  const [emergencyReason, setEmergencyReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadGovernanceData();
    }
  }, [isOpen]);

  const loadGovernanceData = async () => {
    try {
      setIsLoading(true);
      const status = DynamicGovernanceConfig.getGovernanceStatus();
      const history = DynamicGovernanceConfig.getConfigHistory(10);
      const proposals = DynamicGovernanceConfig.getPendingProposals();
      
      setGovernanceStatus(status);
      setConfigHistory(history);
      setPendingProposals(proposals);
      setCirculatingSupply(status.circulatingSupply.toString());
      
      console.log('🔍 Loaded pending proposals:', proposals);
    } catch (err) {
      setError('Failed to load governance data: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSupply = async () => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      const result = await DynamicGovernanceConfig.updateCirculatingSupply(
        parseInt(circulatingSupply),
        walletAddress
      );

      if (result.success) {
        // Handle multi-sig proposal creation response
        if (result.proposalId) {
          setSuccess(`✅ Multi-sig proposal created: ${result.proposalId}`);
          await loadGovernanceData();
          
          // Auto-prompt for signing since no auto-signing occurs
          setTimeout(async () => {
            try {
              const proposals = await DynamicGovernanceConfig.getPendingProposals();
              const thisProposal = proposals.find(p => p.id === result.proposalId);
              
              if (thisProposal) {
                // Check blockchain signatures instead of legacy signatures
                const hasBlockchainSignature = thisProposal.blockchainSignatures && 
                  thisProposal.blockchainSignatures.some(sig => sig.signer === walletAddress);
                
                if (!hasBlockchainSignature) {
                  setSuccess(prev => prev + `\n\n🔐 Ready to sign with blockchain transaction? Click "Sign with Blockchain Transaction" below to create immutable proof of your approval.`);
                }
              }
            } catch (err) {
              console.warn('Could not check signing status:', err);
              // Always show prompt if we can't check status
              setSuccess(prev => prev + `\n\n🔐 Ready to sign with blockchain transaction? Click "Sign with Blockchain Transaction" below.`);
            }
          }, 1000);
        } 
        // Handle direct execution response
        else if (result.newSupply !== undefined) {
          setSuccess(`Circulating supply updated to ${result.newSupply.toLocaleString()} tokens`);
          if (result.phaseChange) {
            setSuccess(prev => prev + ` (Phase changed!)`);
          }
          await loadGovernanceData();
        }
        // Fallback for other success cases
        else {
          setSuccess('Supply update operation completed successfully');
          await loadGovernanceData();
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to update supply: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetOverride = async () => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      const result = await DynamicGovernanceConfig.setAdminOverride(
        overrideParameter,
        parseInt(overrideValue),
        walletAddress
      );

      if (result.success) {
        setSuccess(`Override set: ${result.parameter} = ${result.value}`);
        setOverrideParameter('');
        setOverrideValue('');
        await loadGovernanceData();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to set override: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveOverride = async (parameter) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      const result = await DynamicGovernanceConfig.removeAdminOverride(
        parameter,
        walletAddress
      );

      if (result.success) {
        setSuccess(`Override removed: ${parameter}`);
        await loadGovernanceData();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to remove override: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignProposal = async (proposalId) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      // Enhanced wallet adapter validation
      const walletAdapter = window.solana;
      if (!walletAdapter) {
        throw new Error('Phantom wallet not detected. Please install Phantom wallet.');
      }
      if (!walletAdapter.isConnected) {
        throw new Error('Wallet not connected. Please connect your wallet first.');
      }
      if (!walletAdapter.publicKey) {
        throw new Error('Wallet public key not available. Please reconnect your wallet.');
      }

      console.log(`🔐 Starting blockchain signature for proposal: ${proposalId}`);
      setSuccess('🔐 Preparing blockchain transaction for signature...');

      // Use blockchain-based signing with enhanced error handling
      const result = await DynamicGovernanceConfig.signProposalWithBlockchain(
        proposalId, 
        walletAddress, 
        walletAdapter
      );
      
      if (result.success) {
        const explorerLink = `https://explorer.solana.com/tx/${result.signature}?cluster=devnet`;
        setSuccess(
          `✅ Proposal signed on blockchain!\n` +
          `Transaction: ${result.signature.substring(0, 8)}...\n` +
          `Signatures: ${result.signaturesCount}/${result.requiredSignatures}\n` +
          `View on Solana Explorer: ${explorerLink}`
        );
        await loadGovernanceData(); // Refresh data
        
        // Check if proposal is ready for execution
        if (result.signaturesCount >= result.requiredSignatures) {
          setTimeout(() => {
            setSuccess(prev => prev + `\n\n🚀 Proposal has enough signatures! Ready to execute.`);
          }, 2000);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('❌ Signing error:', err);
      let errorMessage = err.message;
      
      // Handle specific error types
      if (errorMessage.includes('Bf is not defined')) {
        errorMessage = 'Wallet adapter error detected. Please refresh the page and try again.';
      } else if (errorMessage.includes('User rejected')) {
        errorMessage = 'Transaction was cancelled by user.';
      } else if (errorMessage.includes('already been processed')) {
        errorMessage = 'Transaction already processed. This may be a duplicate - please refresh and try again.';
      }
      
      setError('Failed to sign proposal: ' + errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExecuteProposal = async (proposalId) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      const result = await DynamicGovernanceConfig.executeMultiSigProposal(proposalId, walletAddress);
      
      if (result.success) {
        setSuccess(`Proposal ${proposalId} executed successfully!`);
        await loadGovernanceData(); // Refresh data
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to execute proposal: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelProposal = async (proposalId, reason) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      const result = await DynamicGovernanceConfig.createCancelProposal(
        proposalId,
        reason,
        walletAddress
      );
      
      if (result.success) {
        setSuccess(`✅ Cancel proposal created: ${result.proposalId}. Requires 3/3 signatures to execute.`);
        await loadGovernanceData();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to create cancel proposal: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearProposals = async () => {
    try {
      setError('🚨 SECURITY WARNING: Direct proposal clearing is disabled for security.');
      setSuccess('Use individual proposal cancellation instead. Each cancellation requires 3/3 multi-signature approval.');
    } catch (err) {
      setError('Operation not available: ' + err.message);
    }
  };

  const handleResetMultiSig = async () => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      const reason = prompt('Enter reason for resetting multi-sig state (requires 3/3 signatures):');
      if (!reason) {
        setIsLoading(false);
        return;
      }

      const result = await DynamicGovernanceConfig.createResetProposal(reason, walletAddress);
      
      if (result.success) {
        setSuccess(`✅ Reset proposal created: ${result.proposalId}. Requires 3/3 blockchain signatures to execute.`);
        await loadGovernanceData();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to create reset proposal: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmergencyMode = async (enable) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      let result;
      if (enable) {
        result = await DynamicGovernanceConfig.enableEmergencyMode(
          walletAddress,
          emergencyReason
        );
      } else {
        result = await DynamicGovernanceConfig.disableEmergencyMode(
          walletAddress
        );
      }

      if (result.success) {
        setSuccess(enable ? 'Emergency mode enabled' : 'Emergency mode disabled');
        setEmergencyReason('');
        await loadGovernanceData();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to toggle emergency mode: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const isAdmin = DynamicGovernanceConfig.isAdmin(walletAddress);
  
  // Admin check performed — debug logs removed for security

  return (
    <div className="governance-admin-overlay">
      <div className="governance-admin-modal">
        <div className="governance-admin-header">
          <h2>🏛️ Governance Administration</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="governance-admin-content">
          {!isAdmin && (
            <div className="admin-warning">
              ⚠️ Admin access required to modify governance parameters
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {isLoading && <div className="loading">Loading...</div>}

          {governanceStatus && (
            <div className="governance-status">
              <h3>📊 Current Status</h3>
              <div className="status-grid">
                <div className="status-item">
                  <label>Phase:</label>
                  <span className={`phase-badge ${governanceStatus.phase.toLowerCase().replace(' ', '-')}`}>
                    {governanceStatus.phase}
                  </span>
                </div>
                <div className="status-item">
                  <label>Circulating Supply:</label>
                  <span>{governanceStatus.circulatingSupply.toLocaleString()} ZNOOZINU</span>
                </div>
                <div className="status-item">
                  <label>Emergency Mode:</label>
                  <span className={governanceStatus.isEmergencyMode ? 'emergency-active' : 'emergency-inactive'}>
                    {governanceStatus.isEmergencyMode ? '🚨 ACTIVE' : '✅ Inactive'}
                  </span>
                </div>
              </div>

              <div className="thresholds-section">
                <h4>Current Thresholds</h4>
                <div className="thresholds-grid">
                  <div className="threshold-item">
                    <label>Min to Vote:</label>
                    <span>{governanceStatus.thresholds.minStakeToVote} tokens</span>
                  </div>
                  <div className="threshold-item">
                    <label>Min to Propose:</label>
                    <span>{governanceStatus.thresholds.minStakeToPropose} tokens</span>
                  </div>
                  <div className="threshold-item">
                    <label>Quorum:</label>
                    <span>{governanceStatus.thresholds.quorumThreshold} tokens</span>
                  </div>
                  <div className="threshold-item">
                    <label>Voting Cap:</label>
                    <span>{governanceStatus.thresholds.votingPowerCap} tokens</span>
                  </div>
                  <div className="threshold-item">
                    <label>Voting Period:</label>
                    <span>{governanceStatus.thresholds.votingPeriodDays} days</span>
                  </div>
                </div>
              </div>

              {governanceStatus.adminOverrides.length > 0 && (
                <div className="overrides-section">
                  <h4>🔧 Active Overrides</h4>
                  {governanceStatus.adminOverrides.map(override => (
                    <div key={override} className="override-item">
                      <span>{override}</span>
                      {isAdmin && (
                        <button 
                          onClick={() => handleRemoveOverride(override)}
                          className="remove-override-btn"
                          disabled={isLoading}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Pending Multi-Signature Proposals Section */}
          {isAdmin && pendingProposals.length > 0 && (
            <div className="pending-proposals-section">
              <div className="section-header">
                <h3>📋 Pending Multi-Signature Proposals</h3>
                <div className="proposal-controls">
                  <button 
                    onClick={handleClearProposals}
                    disabled={isLoading}
                    className="btn-warning"
                    title="Clear all pending proposals for testing"
                  >
                    🧹 Clear Proposals
                  </button>
                  <button 
                    onClick={handleResetMultiSig}
                    disabled={isLoading}
                    className="btn-danger"
                    title="Create multi-sig proposal to reset state (requires 3/3 signatures)"
                  >
                    🔄 Request Reset (3/3)
                  </button>
                </div>
              </div>
              <div className="proposals-list">
                {pendingProposals.map(proposal => (
                  <div key={proposal.id} className="proposal-item">
                    <div className="proposal-header">
                      <h4>🔄 {proposal.action.replace('_', ' ')}</h4>
                      <span className="proposal-status">{proposal.status}</span>
                    </div>
                    <div className="proposal-details">
                      <p><strong>Proposer:</strong> {proposal.proposer.substring(0, 8)}...</p>
                      <p><strong>Created:</strong> {new Date(proposal.createdAt).toLocaleString()}</p>
                      <p><strong>Expires:</strong> {new Date(proposal.expiresAt).toLocaleString()}</p>
                      <p><strong>Blockchain Signatures:</strong> {proposal.blockchainSignatures ? proposal.blockchainSignatures.length : 0}/{DynamicGovernanceConfig.MULTISIG_CONFIG?.REQUIRED_SIGNATURES || 3}</p>
                      
                      {/* Show blockchain signatures if available */}
                      {proposal.blockchainSignatures && proposal.blockchainSignatures.length > 0 && (
                        <div className="blockchain-signatures">
                          <p><strong>🔗 Blockchain Signatures:</strong></p>
                          {proposal.blockchainSignatures.map((sig, index) => (
                            <div key={index} className="signature-item">
                              <span>✅ {sig.signer.substring(0, 8)}...</span>
                              <a 
                                href={`https://explorer.solana.com/tx/${sig.signature}?cluster=devnet`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="signature-link"
                              >
                                {sig.signature.substring(0, 8)}...
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {proposal.params && (
                        <p><strong>Parameters:</strong> {JSON.stringify(proposal.params, null, 2)}</p>
                      )}
                    </div>
                    
                    <div className="proposal-actions">
                      {(() => {
                        const hasBlockchainSignature = proposal.blockchainSignatures && 
                          proposal.blockchainSignatures.some(sig => sig.signer === walletAddress);
                        const blockchainSignatureCount = proposal.blockchainSignatures ? proposal.blockchainSignatures.length : 0;
                        const requiredSignatures = 3;

                        return (
                          <>
                            {!hasBlockchainSignature && (
                              <button 
                                onClick={() => handleSignProposal(proposal.id)}
                                disabled={isLoading}
                                className="sign-btn"
                              >
                                🔐 Sign with Blockchain Transaction
                              </button>
                            )}
                            {hasBlockchainSignature && (
                              <span className="already-signed">✅ Signed on Blockchain</span>
                            )}
                            {blockchainSignatureCount >= requiredSignatures && (
                              <button 
                                onClick={() => handleExecuteProposal(proposal.id)}
                                disabled={isLoading}
                                className="execute-btn"
                              >
                                🚀 Execute Proposal
                              </button>
                            )}
                            <button 
                              onClick={() => {
                                const reason = prompt('Enter reason for canceling this proposal:');
                                if (reason) {
                                  handleCancelProposal(proposal.id, reason);
                                }
                              }}
                              disabled={isLoading}
                              className="cancel-proposal-btn"
                              title="Create multi-sig proposal to cancel this proposal (requires 3/3 signatures)"
                            >
                              🚫 Request Cancellation
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {isAdmin && (
            <div className="admin-controls">
              <div className="control-section">
                <h3>📈 Update Circulating Supply</h3>
                <div className="input-group">
                  <input
                    type="number"
                    value={circulatingSupply}
                    onChange={(e) => setCirculatingSupply(e.target.value)}
                    placeholder="Enter circulating supply"
                    min="0"
                    max="1000000000"
                  />
                  <button 
                    onClick={handleUpdateSupply}
                    disabled={isLoading || !circulatingSupply}
                    className="update-btn"
                  >
                    Update Supply
                  </button>
                </div>
                <small>Current max supply: 1,000,000,000 ZNOOZINU</small>
              </div>

              <div className="control-section">
                <h3>🔧 Parameter Override</h3>
                <div className="override-form">
                  <select
                    value={overrideParameter}
                    onChange={(e) => setOverrideParameter(e.target.value)}
                  >
                    <option value="">Select parameter</option>
                    <option value="MIN_STAKE_TO_VOTE">Min Stake to Vote</option>
                    <option value="MIN_STAKE_TO_PROPOSE">Min Stake to Propose</option>
                    <option value="QUORUM_THRESHOLD">Quorum Threshold</option>
                    <option value="VOTING_POWER_CAP">Voting Power Cap</option>
                  </select>
                  <input
                    type="number"
                    value={overrideValue}
                    onChange={(e) => setOverrideValue(e.target.value)}
                    placeholder="Override value"
                    min="0"
                  />
                  <button 
                    onClick={handleSetOverride}
                    disabled={isLoading || !overrideParameter || !overrideValue}
                    className="override-btn"
                  >
                    Set Override
                  </button>
                </div>
              </div>

              <div className="control-section emergency-section">
                <h3>🚨 Emergency Mode</h3>
                {!governanceStatus?.isEmergencyMode ? (
                  <div className="emergency-form">
                    <input
                      type="text"
                      value={emergencyReason}
                      onChange={(e) => setEmergencyReason(e.target.value)}
                      placeholder="Reason for emergency mode"
                    />
                    <button 
                      onClick={() => handleEmergencyMode(true)}
                      disabled={isLoading || !emergencyReason}
                      className="emergency-enable-btn"
                    >
                      Enable Emergency Mode
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={() => handleEmergencyMode(false)}
                    disabled={isLoading}
                    className="emergency-disable-btn"
                  >
                    Disable Emergency Mode
                  </button>
                )}
                <small>Emergency mode sets minimal thresholds for urgent governance</small>
              </div>
            </div>
          )}

          {configHistory.length > 0 && (
            <div className="config-history">
              <h3>📜 Recent Changes</h3>
              <div className="history-list">
                {configHistory.map((entry, index) => (
                  <div key={index} className="history-item">
                    <div className="history-timestamp">{entry.timestamp}</div>
                    <div className="history-action">{entry.action}</div>
                    <div className="history-details">
                      {entry.action === 'SUPPLY_UPDATE' && (
                        <span>{entry.oldSupply?.toLocaleString()} → {entry.newSupply?.toLocaleString()} tokens</span>
                      )}
                      {entry.action === 'ADMIN_OVERRIDE' && (
                        <span>{entry.parameter}: {entry.oldValue} → {entry.newValue}</span>
                      )}
                      {entry.action === 'EMERGENCY_MODE_ENABLED' && (
                        <span>Reason: {entry.reason}</span>
                      )}
                    </div>
                    <div className="history-user">{entry.updatedBy || entry.adminWallet}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GovernanceAdmin;
